/**
 * Chakra-compatible style objects for the AI Chat widget.
 *
 * These override the default @chatscope styles to match the Euka
 * design system (bark) look and feel.
 */

import type { SystemStyleObject } from "@chakra-ui/react";

/** Floating toggle button at bottom-right, above the HelpButton */
export const chatToggleButtonStyles: SystemStyleObject = {
  position: "fixed",
  bottom: "120px",
  right: "24px",
  zIndex: "tooltip",
  cursor: "pointer",
  width: "56px",
  height: "56px",
  borderRadius: "full",
  bg: "action.600",
  color: "base.200",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "lg",
  transition: "all 0.2s ease",
  _hover: {
    bg: "action.700",
    transform: "scale(1.05)"
  }
};

/** The floating chat window container */
export const chatWindowStyles: SystemStyleObject = {
  position: "fixed",
  bottom: "120px",
  right: "24px",
  width: { base: "calc(100vw - 32px)", md: "400px" },
  height: { base: "60vh", md: "520px" },
  maxHeight: "70vh",
  zIndex: "tooltip",
  borderRadius: "xl",
  overflow: "hidden",
  boxShadow: "2xl",
  border: "1px solid",
  borderColor: "lines",
  bg: "canvas",
  display: "flex",
  flexDirection: "column"
};

/** Chat window header bar */
export const chatHeaderStyles: SystemStyleObject = {
  bg: "action.600",
  color: "base.200",
  px: 4,
  py: 3,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0
};

/** Message area wrapper */
export const chatBodyStyles: SystemStyleObject = {
  flex: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column"
};

/** CSS overrides for @chatscope components - applied as a global style */
export const chatscopeCssOverrides = `
  /* Reset chatscope container styles to work with Chakra */
  .cs-main-container {
    border: none !important;
    background: transparent !important;
    height: 100% !important;
    font-family: inherit !important;
  }

  .cs-chat-container {
    height: 100% !important;
    background: transparent !important;
  }

  .cs-message-list {
    background: transparent !important;
    padding: 12px !important;
  }

  /* User messages */
  .cs-message--outgoing .cs-message__content {
    background-color: var(--euka-colors-action-600) !important;
    color: white !important;
    border-radius: 16px 16px 4px 16px !important;
    font-size: 14px !important;
    line-height: 1.5 !important;
  }

  /* Assistant messages */
  .cs-message--incoming .cs-message__content {
    background-color: var(--euka-colors-base-100, #f3f4f6) !important;
    color: var(--euka-colors-base-700, #374151) !important;
    border-radius: 16px 16px 16px 4px !important;
    font-size: 14px !important;
    line-height: 1.5 !important;
  }

  /* Message input area */
  .cs-message-input {
    border-top: 1px solid var(--euka-colors-lines-400, #e5e7eb) !important;
    background: transparent !important;
    padding: 8px !important;
  }

  .cs-message-input__content-editor-wrapper {
    background: white !important;
    border: 1px solid var(--euka-colors-lines-400, #e5e7eb) !important;
    border-radius: 12px !important;
    margin: 0 !important;
  }

  .cs-message-input__content-editor {
    background: transparent !important;
    font-size: 14px !important;
    font-family: inherit !important;
    padding: 8px 12px !important;
  }

  .cs-message-input__content-editor[data-placeholder]:empty::before {
    color: var(--euka-colors-base-400, #9ca3af) !important;
    font-family: inherit !important;
  }

  /* Send button */
  .cs-button--send {
    color: var(--euka-colors-action-500) !important;
    align-self: center !important;
  }

  /* Typing indicator */
  .cs-typing-indicator {
    background: transparent !important;
    padding: 8px 12px !important;
  }

  .cs-typing-indicator__text {
    color: var(--euka-colors-base-500, #6b7280) !important;
    font-size: 13px !important;
  }

  .cs-typing-indicator__dot {
    background-color: var(--euka-colors-action-600) !important;
  }

  /* Scrollbar styling */
  .cs-message-list .scrollbar-container {
    scrollbar-width: thin;
  }
`;
